import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { action, observable } from 'mobx'
import type { ContactAddress } from './ContactAddress'
import {
  type Form1099PaymentsInfo,
  type TaxActivityAssignee,
} from './BusinessActivity'
import {
  type PropertyType,
  type TreatPropertyAs,
} from '@taxfyle/txf-api-client'

export type PropertyInformation =
  | { readonly type: PropertyType.SingleFamily }
  | { readonly type: PropertyType.MultiFamily }
  | { readonly type: PropertyType.VacationRental }
  | { readonly type: PropertyType.Commercial }
  | { readonly type: PropertyType.Land }
  | { readonly type: PropertyType.Royalties }
  | { readonly type: PropertyType.SelfRental }
  | { readonly type: PropertyType.Other; readonly description: string | null }

export type RentalActivityProps = ModelProps<RentalActivity>

export class RentalActivity extends Model<RentalActivity> {
  id = Id.none

  @observable accessor archived = false
  @observable accessor assignedTo: TaxActivityAssignee | null = null
  @observable accessor propertyDescription: string | null = null
  @observable accessor treatAs: TreatPropertyAs | null = null
  @observable accessor percentageOfOwnership: number | null = null
  @observable accessor percentageOfTenantOccupancy: number | null = null
  @observable accessor fairRentalDays: number | null = null
  @observable accessor personalUseDays: number | null = null

  @observable accessor propertyInformation: PropertyInformation | null = null

  @observable accessor propertyPlacedInServiceDuringYear: boolean | null = null
  @observable accessor propertyWasDisposedOfDuringYear: boolean | null = null
  @observable accessor carryToForm8960: boolean | null = null
  @observable accessor mainHomeOrSecondHome: boolean | null = null
  @observable accessor qualifiedJointVenture: boolean | null = null
  @observable accessor electingOutOfBusinessInterestExpenseLimit:
    | boolean
    | null = null
  @observable accessor isTradeOrBusiness: boolean | null = null

  @observable accessor form1099Payments: Form1099PaymentsInfo | null = null

  @observable accessor address: ContactAddress | null = null

  @action.bound
  override set({ ...props }: Partial<RentalActivityProps>): this {
    return super.set(props)
  }

  @action.bound
  archive(): void {
    this.assignedTo = { type: 'UNASSIGNED' }
    this.archived = true
  }

  toProps(): RentalActivityProps {
    return {
      id: this.id,
      archived: this.archived,
      assignedTo: this.assignedTo,
      propertyDescription: this.propertyDescription,
      treatAs: this.treatAs,
      percentageOfOwnership: this.percentageOfOwnership,
      percentageOfTenantOccupancy: this.percentageOfTenantOccupancy,
      fairRentalDays: this.fairRentalDays,
      personalUseDays: this.personalUseDays,
      propertyInformation: this.propertyInformation,
      propertyPlacedInServiceDuringYear: this.propertyPlacedInServiceDuringYear,
      propertyWasDisposedOfDuringYear: this.propertyWasDisposedOfDuringYear,
      carryToForm8960: this.carryToForm8960,
      mainHomeOrSecondHome: this.mainHomeOrSecondHome,
      qualifiedJointVenture: this.qualifiedJointVenture,
      electingOutOfBusinessInterestExpenseLimit:
        this.electingOutOfBusinessInterestExpenseLimit,
      isTradeOrBusiness: this.isTradeOrBusiness,
      form1099Payments: this.form1099Payments,
      address: this.address,
    }
  }
}
